import { FC, ReactNode, useState } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbEdit, TbExternalLink, TbFolderSymlink, TbLink, TbStar, TbStarOff, TbTrash } from 'react-icons/tb';

import { useWorkspaceResourceAccess } from '@/hooks/useWorkspaceResourceAccess';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import RenameResourceModal from '@/shared/components/modals/ResourceActionModals/01-RenameResourceModal';
import MoveResourceToFolderModal from '@/shared/components/modals/ResourceActionModals/02-MoveResourceToFolderModal';
import DeleteResourceModal from '@/shared/components/modals/ResourceActionModals/03-DeleteResourceModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import {
    getWorkspaceResourceStatusToggleLabel,
    WorkspaceResourceStatusActionIcon,
} from '@/shared/components/workspace-resources';
import { useMemoStore } from '@/stores';
import { Memo } from '@/types';

import { getWorkspaceResourceRemovalActionLabel, WorkspaceResourceActionMenuItem } from '../helpers';
import WorkspaceResourceActionsDropdown from '../WorkspaceResourceActionsDropdown';

interface MemosActionsDropdownProps {
    memo: Memo;
    children: ReactNode;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
}

const MemosActionsDropdown: FC<MemosActionsDropdownProps> = ({ memo, children, onOpenChange, open }): JSX.Element => {
    const [renameOpen, setRenameOpen] = useState(false);
    const [moveToFolderVisible, setMoveToFolderVisible] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const { memos, toggleMemoFavorite, toggleMemoArchive, toggleMemoLock, duplicateMemo } = useMemoStore();
    const access = useWorkspaceResourceAccess('memos', memo?.id);
    const createAccess = useWorkspaceResourceCreateAccess('memos', memos?.length ?? 0);

    const handleMemoFavoriteToggle = async () => {
        toggleMemoFavorite(memo?.id, !memo?.favorited);
    };

    const handleMemoFavoriteLock = async () => {
        toggleMemoLock(memo?.id, !memo?.locked);
    };

    const handleMemoFavoriteArchive = async () => {
        toggleMemoArchive(memo?.id, !memo?.archived);
    };

    const handleDuplicateMemo = async () => {
        if (createAccess.disabled) {
            return;
        }

        duplicateMemo(memo?.id);
    };

    const items: WorkspaceResourceActionMenuItem[] = [
        {
            key: 'favorited',
            icon: memo?.favorited ? <TbStarOff size={'1rem'} /> : <TbStar size={'1rem'} />,
            label: memo?.favorited ? 'Remove from Favorites' : 'Add to Favorites',
            onClick: handleMemoFavoriteToggle,
            permission: 'update',
        },
        {
            key: 'locked',
            icon: <WorkspaceResourceStatusActionIcon active={memo?.locked} status='locked' />,
            label: getWorkspaceResourceStatusToggleLabel('locked', memo?.locked),
            onClick: handleMemoFavoriteLock,
            permission: 'manage',
        },
        {
            key: 'archived',
            icon: <WorkspaceResourceStatusActionIcon active={memo?.archived} status='archived' />,
            label: getWorkspaceResourceStatusToggleLabel('archived', memo?.archived),
            onClick: handleMemoFavoriteArchive,
            permission: 'manage',
        },
        {
            key: 'copyLink',
            icon: <TbLink size={'1rem'} />,
            label: 'Copy Link',
            onClick: () => {
                navigator.clipboard
                    .writeText(
                        `${
                            import.meta.env.DEV ? 'http://localhost:3000' : 'https://dashboard.countercyclical.io'
                        }/memos/${memo?.id}`,
                    )
                    .then(
                        () => {
                            toast('Link Copied', {
                                icon: <TbLink size='1.125rem' style={{ marginRight: '0.5rem' }} />,
                                description: memo?.title,
                            });
                        },
                        (error) => {
                            toast('Error Copying Link', {
                                description: memo?.title,
                            });

                            captureException(error);
                        },
                    );
            },
            permission: 'read',
        },
        {
            key: 'openInNewWindow',
            icon: <TbExternalLink size={'1rem'} />,
            label: 'Open in New Window',
            onClick: () => {
                window.open(`/memos/${memo?.id}`, '_blank');
            },
            permission: 'read',
        },
        {
            key: 'rename',
            icon: <TbEdit size={'1rem'} />,
            label: 'Rename',
            onClick: () => {
                setRenameOpen(true);
            },
            permission: 'update',
        },
        {
            key: 'duplicate',
            disabled: createAccess.disabled,
            icon: <BiDuplicate size={'1rem'} />,
            label: 'Duplicate',
            onClick: handleDuplicateMemo,
            permission: 'read',
        },
        // {
        //     key: 'templatized',
        //     icon: templatized ? <TbIcons size={'1rem'} /> : <TbIconsOff size={'1rem'} />,
        //     label: templatized ? 'Revert Template' : 'Convert to Template',
        //     onClick: () => {
        //         authorizedAxiosInstance
        //             .put(`/api/memos/${memo?.id}`, {
        //                 isTemplate: !templatized,
        //             })
        //             .then((res: AxiosResponse) => {
        //                 if (res.status === 200) {
        //                     setArchived(res?.data?.isTemplate);

        //                     toast(`Memo ${archived ? 'Template Reverted' : 'Converted to Template'}`, {
        //                         icon: res?.data?.isTemplate ? <TbIconsOff size={'1rem'} /> : <TbIcons size={'1rem'} />,
        //                         description: res?.data?.editedName,
        //                     });
        //                 }
        //             });
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'transfer',
        //     icon: <TbArrowsHorizontal size="1rem" />,
        //     label: 'Transfer',
        //     onClick: () => {
        //         return;
        //     },
        //     disabled: true,
        // },
        // {
        //     key: 'export',
        //     icon: <TbFileExport size={'1rem'} style={{ transform: 'rotate(-90deg)' }} />,
        //     label: 'Export',
        //     onClick: () => {
        //         return;
        //     },
        //     disabled: true,
        // },
        {
            key: 'moveToFolder',
            icon: <TbFolderSymlink size='1rem' />,
            label: 'Move to Folder',
            onClick: () => {
                setMoveToFolderVisible(true);
            },
            permission: 'update',
        },
        {
            key: 'delete',
            icon: <TbTrash size={'1rem'} />,
            label: getWorkspaceResourceRemovalActionLabel('memos'),
            onClick: () => {
                setDeleteOpen(true);
            },
            danger: true,
            permission: 'manage',
        },
    ];

    return (
        <>
            <WorkspaceResourceActionsDropdown
                access={access}
                history={{ resourceId: memo.id, resourceType: 'memos', title: memo.title || 'Untitled Memo' }}
                items={items}
                onOpenChange={onOpenChange}
                open={open}
                resourceLabel='Memo'
            >
                {children}
            </WorkspaceResourceActionsDropdown>
            <RenameResourceModal resource={memo} singularResource='memo' open={renameOpen} setOpen={setRenameOpen} />
            <MoveResourceToFolderModal
                resource={memo}
                singularResource='memo'
                open={moveToFolderVisible}
                setOpen={setMoveToFolderVisible}
            />
            <DeleteResourceModal resource={memo} singularResource='memo' open={deleteOpen} setOpen={setDeleteOpen} />
        </>
    );
};

export default MemosActionsDropdown;
